import { useEffect, useRef } from 'react';

import { ReceiptNoteType } from '../../models/enums/receipt-note-type';
import { fetchDraftNotes } from '../../actions/internal-notes-actions';
import { useThunkDispatch } from '../../../store/use-thunk-dispatch';

export interface PaloDraftNoteIds {
    accountIds: number[];
    contactIds: number[];
    productIds: number[];
}

type LooseIds = (number | null | undefined)[];

/** Each id once, in a fixed order, so equal picks compare equal. */
const clean = (ids: LooseIds): number[] =>
    [...new Set(ids.filter((id): id is number => 'number' === typeof id && id > 0))].sort((a, b) => a - b);

export const paloDraftNoteIds = (ids: {
    accountIds?: LooseIds;
    contactIds?: LooseIds;
    productIds?: LooseIds;
}): PaloDraftNoteIds => ({
    accountIds: clean(ids.accountIds ?? []),
    contactIds: clean(ids.contactIds ?? []),
    productIds: clean(ids.productIds ?? []),
});

/**
 * Asks for the notes of what a form holds, once the picking has settled.
 *
 * The notes of a supplier or customer used to appear only once the document
 * was saved, because the head loaded them for the saved document. A notice
 * like "do not pass on the energy surcharge" is needed while the document is
 * being written (#2236, FRM-03).
 *
 * Asks again only when the ids change, and keeps only the answer to the last
 * question: an answer for a supplier picked before must not overwrite the one
 * for the supplier picked now.
 */
export const usePaloDraftNotices = (noteType: ReceiptNoteType, ids: PaloDraftNoteIds): void => {
    const dispatch = useThunkDispatch();
    const asked = useRef(0);
    const key = JSON.stringify(ids);

    useEffect(() => {
        const question = ++asked.current;
        const timer = setTimeout(() => {
            dispatch(fetchDraftNotes(noteType, JSON.parse(key), () => question === asked.current));
        }, 300);

        return () => clearTimeout(timer);
    }, [dispatch, noteType, key]);

    // Leaving the mask: an answer still on its way belongs to no one.
    useEffect(() => () => void ++asked.current, []);
};
